import { Component, DOCUMENT, computed, inject, input } from '@angular/core';
import { Marked } from 'marked';
import { ConversationEntry } from 'app/shared/agent/agent-conversation.store';
import { AgentChartView } from 'app/shared/agent/ui/agent-chart/agent-chart';

/**
 * Renders a single conversation entry. Visual treatment follows the Story
 * design: assistant turns are left-aligned text rendered from Markdown (lists,
 * bold, tables), user turns are
 * right-aligned green bubbles, and tool/system/error markers are muted meta
 * lines. Reusable across the admin tester and the Story feature.
 */
@Component({
  selector: 'app-agent-message',
  imports: [AgentChartView],
  host: {
    class: 'agent-message',
    '[attr.data-kind]': 'entry().kind',
  },
  templateUrl: './agent-message-bubble.html',
  styleUrls: ['./agent-message-bubble.scss'],
})
export class AgentMessageBubble {
  readonly entry = input.required<ConversationEntry>();
  private readonly document = inject(DOCUMENT);

  /** Exposed for potential consumers that want kind-driven styling. */
  protected readonly kind = computed(() => this.entry().kind);

  /**
   * Parsed synchronously so the text is in the DOM on the same render: the chat thread
   * measures it right away to keep the newest message in view while it streams.
   * Single line breaks stay line breaks, as in the plain-text replies the agents wrote before.
   */
  protected readonly assistantHtml = computed(() =>
    AgentMessageBubble.markdown.parse(this.entry().text ?? '', { async: false }),
  );

  private static readonly markdown = new Marked({ gfm: true, breaks: true });

  /** A link in an answer must not navigate away from the conversation. */
  protected openLinksInNewTab(event: MouseEvent): void {
    const link = (event.target as HTMLElement | null)?.closest('a');
    if (!link?.href) return;
    event.preventDefault();
    this.document.defaultView?.open(link.href, '_blank', 'noopener');
  }
}
